import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import SafeIcon from '../../common/SafeIcon';
import { TEAM_K9_LOGO_URL } from '../../common/assets';
import { format, addDays, parseISO } from 'date-fns';

const OwnerSignup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [trainer, setTrainer] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [createdPlanId, setCreatedPlanId] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formData, setFormData] = useState({
    owner_name: '',
    dog_name: '',
    email: '',
    phone: ''
  });

  const fetchTrainerAndPricing = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('trainer_profiles')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1)
        .single();
      
      if (fetchError) throw fetchError;
      if (data) {
        setTrainer(data);
        setTiers(data.pricing_tiers || []);
      }
    } catch (err) {
      console.error('Error fetching trainer:', err);
      setError('Could not load trainer information.');
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrainerAndPricing();
  }, [fetchTrainerAndPricing]);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      setError('Please agree to the Terms & Conditions to continue.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      if (!trainer?.master_plan_id) {
        throw new Error('Trainer configuration incomplete. Please contact the trainer.');
      }

      // 1. Create the Client
      const { data: client, error: clientError } = await supabase
        .from('training_clients')
        .insert([{
          trainer_id: trainer.id,
          owner_name: formData.owner_name.trim(),
          dog_name: formData.dog_name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          selected_tier: selectedTier.id,
          signup_status: 'completed',
          messaging_enabled: false
        }])
        .select()
        .single();

      if (clientError) throw clientError;

      // 2. Fetch Master Plan and its tasks to calculate duration
      const { data: masterPlan } = await supabase
        .from('training_plans')
        .select('*')
        .eq('id', trainer.master_plan_id)
        .single();

      const { data: masterTasks } = await supabase
        .from('training_tasks')
        .select('*')
        .eq('plan_id', trainer.master_plan_id);

      const startDate = format(new Date(), 'yyyy-MM-dd');
      let endDate = startDate;

      if (masterTasks?.length > 0) {
        const maxDays = masterTasks.reduce((max, t) => {
          const days = ((t.week_number - 1) * 7) + (t.day_number - 1);
          return Math.max(max, days);
        }, 0);
        endDate = format(addDays(parseISO(startDate), maxDays), 'yyyy-MM-dd');
      }

      // 3. Create the Plan with calculated End Date
      const { data: newPlan, error: planError } = await supabase
        .from('training_plans')
        .insert([{
          client_id: client.id,
          plan_name: `${selectedTier.name} for ${formData.dog_name}`,
          start_date: startDate,
          end_date: endDate,
          price: Number(selectedTier.price),
          payment_link: selectedTier.payment_link,
          is_paid: false,
          goals: masterPlan?.goals,
          resource_url: masterPlan?.resource_url
        }])
        .select()
        .single();

      if (planError) throw planError;

      // 4. Create Pending Payment
      await supabase.from('trainer_payments_20250218').insert([{
        trainer_id: trainer.id,
        client_id: client.id,
        plan_id: newPlan.id,
        amount: Number(selectedTier.price),
        status: 'pending',
        notes: `Signup for ${selectedTier.name}`
      }]);

      // 5. Clone tasks and map their dates
      if (masterTasks?.length > 0) {
        const clonedTasks = masterTasks.map(task => {
          const { id, created_at, updated_at, is_completed, session_score, video_submission_url, video_reviewed, completed_checkboxes, ...taskData } = task;
          
          const daysToAdd = ((task.week_number - 1) * 7) + (task.day_number - 1);
          const taskDate = format(addDays(parseISO(startDate), daysToAdd), 'yyyy-MM-dd');

          return {
            ...taskData,
            plan_id: newPlan.id,
            task_date: taskDate,
            is_completed: false,
            session_score: null,
            video_submission_url: null,
            video_reviewed: false,
            completed_checkboxes: []
          };
        });
        await supabase.from('training_tasks').insert(clonedTasks);
      }

      setCreatedPlanId(client.id);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent" />
    </div>
  );

  if (step === 3) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 text-center">
      <div className="max-w-md w-full bg-white p-12 rounded-[3.5rem] shadow-2xl border border-gray-100 animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <SafeIcon name="Award" className="w-10 h-10" />
        </div>
        <h2 className="text-3xl font-black text-gray-900 mb-2">Registration Complete!</h2>
        <p className="text-gray-500 font-medium mb-8 leading-relaxed">
          To unlock the training portal for <b>{formData.dog_name}</b>, please complete the payment for the <b>{selectedTier.name}</b> package.
        </p>
        <div className="space-y-4">
          <a 
            href={selectedTier.payment_link} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="w-full bg-indigo-600 text-white py-5 rounded-2xl font-black shadow-xl shadow-indigo-100 flex items-center justify-center gap-3 hover:bg-indigo-700 hover:scale-[1.02] transition-all"
          >
            Pay ${selectedTier.price} & Unlock Portal <SafeIcon name="ExternalLink" />
          </a>
          <button 
            onClick={() => navigate(`/portal/plan/${createdPlanId}`)}
            className="w-full text-indigo-600 font-black text-sm py-4 hover:bg-indigo-50 rounded-2xl transition-all"
          >
            I've already paid, go to my portal
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <img src={TEAM_K9_LOGO_URL} alt="Team K9" className="w-24 h-24 mx-auto mb-6" />
          <h1 className="text-4xl font-black text-gray-900 mb-2">Transform Your Dog</h1>
          <p className="text-gray-500 font-medium tracking-tight">
            Join the elite community of dog owners training with <span className="text-indigo-600 font-black">{trainer?.business_name || 'Team K9'}</span>
          </p>
        </div>

        {step === 1 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 justify-center">
            {tiers.map(tier => (
              <div key={tier.id} className={`p-8 rounded-[2.5rem] border-2 transition-all hover:scale-[1.02] bg-white flex flex-col ${tier.popular ? 'border-indigo-600 shadow-2xl shadow-indigo-100' : 'border-gray-100 shadow-sm'}`}>
                {tier.popular && <span className="self-start text-[10px] font-black uppercase text-white bg-indigo-600 px-3 py-1 rounded-full mb-4 tracking-widest">Most Popular</span>}
                <h3 className="text-lg font-black text-gray-900 mb-2">{tier.name}</h3>
                <div className="text-4xl font-black text-gray-900 mb-3">${tier.price}</div>
                {tier.description && <p className="text-gray-500 text-sm font-medium mb-6 leading-relaxed">{tier.description}</p>}
                <ul className="space-y-4 mb-10 flex-1">
                  {(tier.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm font-bold text-gray-600">
                      <SafeIcon name="CheckCircle" className="text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col gap-3 mt-auto">
                  <button 
                    onClick={() => { setSelectedTier(tier); setStep(2); }}
                    className={`w-full py-4 rounded-2xl font-black transition-all ${tier.popular ? 'bg-indigo-600 text-white shadow-lg' : 'bg-gray-50 text-gray-900 hover:bg-gray-100'}`}
                  >
                    Select This Plan
                  </button>
                  {tier.learn_more_url && (
                    <a 
                      href={tier.learn_more_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="w-full py-4 rounded-2xl font-black text-center transition-all bg-white border-2 border-gray-100 text-gray-600 hover:bg-gray-50 hover:border-gray-200"
                    >
                      Learn More
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="max-w-xl mx-auto bg-white p-8 rounded-[2.5rem] shadow-xl border border-gray-100">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-2xl font-black text-gray-900">Owner Details</h3>
              <button 
                onClick={() => { setStep(1); setAgreedToTerms(false); }}
                className="text-xs font-black text-indigo-600 uppercase tracking-widest hover:underline"
              >
                Change Plan
              </button>
            </div>
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-gray-400 ml-1">Your Name</label>
                  <input 
                    required 
                    value={formData.owner_name} 
                    onChange={e => setFormData({ ...formData, owner_name: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl focus:border-indigo-500 outline-none font-bold" 
                    placeholder="Full Name" 
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-gray-400 ml-1">Dog's Name</label>
                  <input 
                    required 
                    value={formData.dog_name} 
                    onChange={e => setFormData({ ...formData, dog_name: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl focus:border-indigo-500 outline-none font-bold" 
                    placeholder="Dog Name" 
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-400 ml-1">Email Address</label>
                <input 
                  type="email" 
                  required 
                  value={formData.email} 
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl focus:border-indigo-500 outline-none font-bold" 
                  placeholder="email@example.com" 
                />
                <p className="text-xs text-gray-500 font-medium ml-1 mt-1">Use the same email here and at checkout or your plan may not unlock automatically</p>
              </div>

              <label className="flex items-start gap-4 cursor-pointer group p-4 bg-gray-50 rounded-2xl border-2 border-transparent hover:border-indigo-100 transition-all mt-6">
                <div className="relative flex items-center mt-0.5">
                  <input 
                    type="checkbox" 
                    required 
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="peer w-6 h-6 rounded-lg text-indigo-600 border-gray-300 focus:ring-indigo-500 transition-all cursor-pointer" 
                  />
                </div>
                <span className="text-[11px] leading-relaxed font-bold text-gray-600 select-none">
                  I understand that all sales are final and non-refundable. I acknowledge that training results are not guaranteed and depend on my consistency and participation. I understand that required training equipment is not included. By continuing, I agree to the {trainer?.terms_url ? (
                    <a href={trainer.terms_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline hover:text-indigo-800" onClick={e => e.stopPropagation()}>Terms & Conditions</a>
                  ) : "Terms & Conditions"}.
                </span>
              </label>

              {error && <p className="text-red-500 text-xs font-bold bg-red-50 p-3 rounded-xl border border-red-100">{error}</p>}
              
              <button 
                type="submit" 
                disabled={loading || !agreedToTerms}
                className="w-full bg-indigo-600 text-white py-5 rounded-2xl font-black shadow-xl mt-6 flex justify-center items-center gap-2 active:scale-95 transition-all disabled:opacity-50 disabled:grayscale"
              >
                {loading ? <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" /> : <>Continue to Payment <SafeIcon name="ArrowRight" /></>}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerSignup;